import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { POType, FulfillmentPathConfig, Marketplace, BuyScoreWeightComponent } from '../types';

// U10 SLICE B (20260719000045): the domain_scope_config arm (domainScopeRows +
// domainScopes + the 5th parallel fetch) RETIRED with the table (fork 3, Sol
// "go."). At 0 live rows — the state since the U1 wipe — domainScopes was
// always {}, so every consumer already saw the fail-safe; the removal makes
// that behavior static. Register: the U10 SLICE B build entry.

interface SettingsCache {
  poTypes: POType[];
  paths: FulfillmentPathConfig[];
  marketplaces: Marketplace[];
  buyScoreWeightComponents: BuyScoreWeightComponent[];
  ts: number;
}

let _cache: SettingsCache = {
  poTypes: [],
  paths: [],
  marketplaces: [],
  buyScoreWeightComponents: [],
  ts: 0,
};
const TTL = 60000;

export interface UseSettingsConfigResult {
  poTypes: POType[];
  allPoTypes: POType[];
  activePaths: FulfillmentPathConfig[];
  allPaths: FulfillmentPathConfig[];
  activePathKeys: string[];
  marketplaces: Marketplace[];
  activeMarketplaces: Marketplace[];
  // Day 91a.5 P2A — Buy Score Weights graduation substrate. Array of bswc rows
  // ordered by sort_order; consumers filter by is_available=true at usage time
  // (ParamsSidebar visibleWeights / Settings ProfileSection initialWeights /
  // BuyingSession bswcTooltipFallback). 4th parallel loader; 60s TTL inherits
  // Decision 35. Type alias BuyScoreWeightComponent at src/types/index.ts
  // (Row<'buy_score_weight_components'>).
  buyScoreWeightComponents: BuyScoreWeightComponent[];
  loading: boolean;
  reload: () => Promise<void>;
}

export function useSettingsConfig(): UseSettingsConfigResult {
  const [poTypes, setPoTypes] = useState<POType[]>(_cache.poTypes);
  const [paths, setPaths] = useState<FulfillmentPathConfig[]>(_cache.paths);
  const [marketplaces, setMarketplaces] = useState<Marketplace[]>(_cache.marketplaces);
  const [buyScoreWeightComponents, setBuyScoreWeightComponents] = useState<
    BuyScoreWeightComponent[]
  >(_cache.buyScoreWeightComponents);
  const [loading, setLoading] = useState<boolean>(_cache.ts === 0);

  const load = useCallback(async (force = false): Promise<void> => {
    if (!force && Date.now() - _cache.ts < TTL && _cache.poTypes.length > 0) {
      setPoTypes(_cache.poTypes);
      setPaths(_cache.paths);
      setMarketplaces(_cache.marketplaces);
      setBuyScoreWeightComponents(_cache.buyScoreWeightComponents);
      setLoading(false);
      return;
    }
    setLoading(true);
    const [pt, fp, mk, bsw] = await Promise.all([
      supabase.from('po_types').select('*').order('sort_order'),
      supabase.from('fulfillment_path_config').select('*').order('sort_order'),
      supabase.from('marketplaces').select('*').order('display_name'),
      supabase.from('buy_score_weight_components').select('*').order('sort_order'),
    ]);
    const ptData = (pt.data ?? []) as unknown as POType[];
    const fpData = (fp.data ?? []) as unknown as FulfillmentPathConfig[];
    const mkData = (mk.data ?? []) as unknown as Marketplace[];
    const bswData = (bsw.data ?? []) as unknown as BuyScoreWeightComponent[];
    _cache = {
      poTypes: ptData,
      paths: fpData,
      marketplaces: mkData,
      buyScoreWeightComponents: bswData,
      ts: Date.now(),
    };
    setPoTypes(ptData);
    setPaths(fpData);
    setMarketplaces(mkData);
    setBuyScoreWeightComponents(bswData);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const reload = useCallback((): Promise<void> => load(true), [load]);

  return {
    poTypes: poTypes.filter((t) => t.is_active),
    allPoTypes: poTypes,
    activePaths: paths.filter((p) => p.is_active),
    allPaths: paths,
    activePathKeys: paths.filter((p) => p.is_active).map((p) => p.path_key),
    marketplaces,
    activeMarketplaces: marketplaces.filter((m) => m.is_active),
    buyScoreWeightComponents,
    loading,
    reload,
  };
}
